/**
 * AttendanceService - Handles attendance and site-related business logic
 */
import CheckinApiHelper from '../Helpers/CheckinApiHelper.js'

class AttendanceService {
  /**
   * Fetch site information by ID
   */
  async getSite (siteId) {
    try {
      const site = await CheckinApiHelper.getSite(siteId)
      return site
    } catch (err) {
      throw new Error(`Failed to fetch site: ${err.message}`)
    }
  }

  /**
   * Get the site ID associated with a tumbler ID, if it isn't a tumbler ID, but
   * is a site ID, it will return the site ID directly
   * @param {Guid} tumblerId
   * @returns Guid siteId
   */
  async getTumblerSiteId (tumblerId) {
    try {
      const siteId = await CheckinApiHelper.GetTumblerSiteId(tumblerId)
      return siteId
    } catch (err) {
      throw new Error(`Failed to fetch tumbler site ID: ${err.message}`)
    }
  }

  /**
   * Submit a tumbler power reading taken from the scanned QR URL.
   *
   * Deliberately does not wrap failures in a rethrown Error the way the other
   * methods here do: telemetry is ancillary and must never surface to a worker,
   * so the rejection is passed through for the caller to swallow.
   *
   * @param {Object} params - tumblerId, batteryLevel, powerSource and the
   * optional qrPayloadVersion, rtc and secret.
   * @returns {Promise<void>} Resolves when accepted; rejects for the caller to ignore.
   */
  async postTelemetry (params) {
    return CheckinApiHelper.postTelemetry(params)
  }

  /**
   * Fetch the latest attendance record for the current user
   */
  async getLatestAttendance () {
    try {
      const attendance = await CheckinApiHelper.getLatestAttendance()
      return attendance
    } catch (err) {
      throw new Error(`Failed to fetch attendance: ${err.message}`)
    }
  }

  /**
   * Check if the user is currently checked in at the specified site
   */
  isCheckedIn (latestAttendance, siteMatch) {
    if (!latestAttendance) {
      return false
    }

    return !!(
      latestAttendance.checkInTime &&
      !latestAttendance.checkOutTime
    )
  }

  /**
   * Check if the latest attendance matches the given site
   */
  doesAttendanceMatchSite (attendance, siteId) {
    if (!siteId) {
      return true
    }
    if (!attendance || !siteId) {
      return false
    }
    return attendance.jobid === siteId || attendance.jobId === siteId
  }

  /**
   * Initialize current user session
   */
  async initializeUser () {
    try {
      return await CheckinApiHelper.getCurrentUser()
    } catch (err) {
      throw new Error(`Failed to initialize user: ${err.message}`)
    }
  }
}

export default AttendanceService
